/**
 * What the web app and the phone app say to each other (plan.md step 60).
 *
 * The app is the web app in a web view; a page cannot schedule a local
 * notification or hold a native push token, so it ASKS the shell. One
 * definition here, so the two sides cannot disagree about a field — and a
 * parser on each side that keeps only what is declared, because a web view
 * will deliver whatever a page posts.
 */
import type { PlannedReminder } from './reminders.js';
import { productById, type AnyProductId } from './products.js';
import { LIVE_CARD_WORDS, type LiveCardAct, type LiveCardPlan, type LiveCardStage, type LiveCardWords } from './liveCard.js';
import { MAX_ARRIVAL_REGIONS, parseArrivalRegion, type ArrivalRegion } from './arrival.js';
import { isWebUrl } from './links.js';
import { WEATHER_CONDITIONS, type WeatherCondition } from './weather.js';
import { MAX_WIDGET_ACTS, MAX_WIDGET_DAYS, MAX_WIDGET_HOURS, MAX_WIDGET_POIS, WIDGET_WORDS, type WidgetDay, type WidgetPlan, type WidgetStage, type WidgetTrack, type WidgetWords } from './widgets.js';

/** Page → shell, through `window.ReactNativeWebView.postMessage`. */
export type ToNative =
  | { type: 'push.register' }
  | { type: 'reminders.set'; editionId: string; reminders: PlannedReminder[] }
  /**
   * The phone app stays upright, except where the page says turning it is
   * welcome — the schedule's grid, which goes full screen on its side (the
   * user, 2026-10-07). `any` lets it turn; `portrait` locks it upright again.
   */
  | { type: 'orientation'; allow: 'portrait' | 'any' }
  /**
   * The screen stays on only where the page asks — the rave's map (the user, 2026-10-30: "we don't
   * need it always on, only on the map"). `false` gives the phone its own sleep back.
   */
  | { type: 'awake'; on: boolean }
  /**
   * Whether somebody is signed in, as the page knows it (the user, 2026-10-26: "it needs to open
   * the last rave if the user was in a rave"): the shell reopens the last rave at launch only
   * for a signed-in person — signed out it opens Find a rave. Sent on every change.
   */
  | { type: 'session'; signedIn: boolean; accountId?: string }
  /*
   * The widgets (step 79): one ATTENDED rave's data, from the app's offline
   * data, while signed in; `widget.clear` takes one rave out (left, or no
   * longer attended) — or, with no `editionId`, all of them; `widget.words`
   * the widgets' own words in the reader's language (sent signed out too, for
   * "Sign in to use MyRave widgets").
   */
  | { type: 'widget.plan'; plan: WidgetPlan }
  | { type: 'widget.clear'; editionId?: string }
  /**
   * The raves this account ATTENDS, from the server's own answer (2026-10-30): every rave
   * the phone keeps that is not among them leaves every widget — one left on another
   * device, or a database copied over, stayed for good.
   */
  | { type: 'widget.keep'; editionIds: string[] }
  /**
   * Your tickets (step 91, the user, 2026-10-29: "kept locally on the phone, never on the server"): the app's
   * OWN store is the one every page shares (each host keeps its own browser storage) and the one a phone's
   * transfer carries to a new phone. `ticket` is a ticket as the page keeps it (JSON, no file); a file goes
   * on its own (`tickets.file`, base64); `tickets.raves` is the raves they belong to, for the account's list.
   */
  | { type: 'tickets.put'; owner: string; id: string; editionId: string; ticket: string }
  | { type: 'tickets.file'; owner: string; id: string; editionId: string; mime: string; base64: string }
  | { type: 'tickets.drop'; owner: string; ids: string[]; fileIds: string[] }
  | { type: 'tickets.raves'; owner: string; raves: string }
  /** Everything kept for this account — the files by id only; `tickets.fileGet` asks for one. */
  | { type: 'tickets.list'; requestId: string; owner: string }
  | { type: 'tickets.fileGet'; requestId: string; owner: string; id: string }
  | { type: 'widget.words'; words: WidgetWords }
  /*
   * In-app purchases (the user, 2026-10-07, evening: in the phone app digital
   * goods are bought through Apple and Google, never Stripe). The page asks;
   * the shell runs the store's sheet and hands the transaction BACK to the
   * page, which sends it to the API with its own session — the shell holds no
   * credentials — and then tells the shell which ones to finish
   * (`iap.finish`), only after the server has verified them.
   */
  /** Buy one product — a catalogue one, or an Independent Rave's price step — for this account (its `appAccountToken`, `GET /billing/store/account`). */
  | { type: 'iap.buy'; requestId: string; product: AnyProductId; appAccountToken: string; editionId?: string; photoId?: string }
  /** Every transaction the store still holds (iOS requires "Restore purchases"); `quiet` = no sheet, on launch. */
  | { type: 'iap.restore'; requestId: string; quiet?: boolean }
  /** The store's own prices — Apple and Google require showing THEIR price. */
  | { type: 'iap.prices'; requestId: string }
  /** The server has answered for these transactions: finish (acknowledge / consume) them. */
  | { type: 'iap.finish'; ids: string[] }
  /** The store's subscription page — where a store plan is cancelled. */
  | { type: 'iap.manage' }
  /*
   * Location and the compass, answered by the APP (the user, 2026-10-12: the
   * iPhone asked three times — once at install, then each rave's address, then
   * "motion and orientation" on the map). iOS asks again for every website in
   * a web view, so on iOS the shell stands in for `navigator.geolocation` and
   * the compass (`mobile/src/geoBridge.ts`), from the permission it was given
   * once. `requestId` names the call; a watch keeps its id until `geo.clear`.
   */
  | { type: 'geo.get'; requestId: string; highAccuracy?: boolean }
  | { type: 'geo.watch'; requestId: string; highAccuracy?: boolean }
  | { type: 'geo.clear'; requestId: string }
  | { type: 'heading.start' }
  /** The always-on card (step 67): show it from this plan, the shell re-plans on its own. */
  | { type: 'card.show'; plan: LiveCardPlan }
  | { type: 'card.end'; editionId: string }
  /** "Follow me" needs location in the background — ask, or stop. */
  | { type: 'location.always'; enable: boolean }
  /** "Tell me when I arrive at a rave" (step 60 task 4): watch these places — `[]` stops. */
  | { type: 'arrival.set'; regions: ArrivalRegion[] }
  /*
   * Invite a friend (step 89): the phone's OWN share sheet — AirDrop on an iPhone, Quick Share
   * on Android, every app — for `navigator.share`, which Android's web view does not have. The
   * shell's shim (`mobile/src/shareShim.ts`) posts it; `share.done` answers. And the screen at
   * full brightness while the invite's QR code shows (`raise: false` puts it back).
   */
  | { type: 'share.open'; requestId: string; title?: string; text?: string; url: string }
  | { type: 'brightness'; raise: boolean }
  /**
   * Step 99: prove this is the genuine MyRave app (App Attest on iPhone, Play Integrity on Android) by
   * answering the server's one-time `challenge`. `cloudProjectNumber` is Google's, for Android.
   */
  | { type: 'integrity.attest'; requestId: string; challenge: string; cloudProjectNumber?: string }
  /**
   * "Continue with Google" in the app (the user, 2026-10-30): the shell shows Google's own account sheet and
   * answers with the signed ID token (`google.signedin`), minted for `webClientId` — the platform's web client,
   * which `POST /auth/google/one-tap` already checks as the audience. Only a shell whose user agent says
   * ` google` is asked (`readNativeApp`).
   */
  | { type: 'google.signin'; requestId: string; webClientId: string }
  /**
   * "A fresh MyRave just dropped" → Update, in the Android APK (the 0.14.74 walk: "the full install wizard in the
   * app, never a link to Expo"): the shell downloads the newest APK from `url` (`https://expo.dev/artifacts/…`
   * only), says how far (`app.install.progress`), then hands it to Android's own installer (`app.install.ready`).
   * An older shell does not answer, and the page opens the link as before.
   */
  | { type: 'app.install'; requestId: string; url: string }
  /**
   * "Continue with Apple" in the app (2026-10-30): Apple's own sheet — one tap and Face ID. Answered with
   * `apple.signedin` (the token names the APP as its audience — `POST /auth/apple/native`). Only a shell whose
   * user agent says ` apple` is asked.
   */
  | { type: 'apple.signin'; requestId: string }
  /**
   * An admin's passkey in the ANDROID app (2026-10-30, the user: the passkey is in iCloud Keychain on the
   * iPhone — "show a barcode so I can scan it on the iPhone"). Android's web view has no WebAuthn, so the
   * page hands the API's WebAuthn options (JSON) to the shell, which asks Android's Credential Manager —
   * a passkey on this phone, or "use a passkey from another device": a QR code the iPhone scans. Answered
   * with `passkey.done` (the credential as WebAuthn JSON, posted to the API as the browser's) or
   * `passkey.failed`. Only a shell whose user agent says ` passkeys` on Android is asked.
   */
  | { type: 'passkey.get'; requestId: string; requestJson: string }
  | { type: 'passkey.create'; requestId: string; requestJson: string };

/** A transaction as the shell hands it to the page: the store's id, its product, and what the API verifies. */
export interface StorePurchase {
  /** The store's transaction id — what `iap.finish` names. */
  id: string;
  /** The STORE product id (`PRODUCTS[…].store.productId`). */
  productId: string;
  /** iOS: the signed transaction (JWS); Android: the purchase token. */
  token: string;
}

/** Shell → page, as a `myrave:native` event on `window`. */
export type FromNative =
  | { type: 'push.token'; token: string; platform: 'ios' | 'android' }
  /**
   * No push address. `reason`: `permission` — the person said no (or did
   * before); `error` — iOS / Android could not give one, with its `message`
   * (the user, 2026-10-07: a refusal and a failure looked the same, so
   * nobody could tell why nothing arrived). An older app sends neither.
   */
  | { type: 'push.denied'; reason?: 'permission' | 'error'; message?: string }
  /** Store transactions to verify — a purchase just made, or a restore. */
  | { type: 'iap.purchases'; requestId?: string; platform: 'ios' | 'android'; purchases: StorePurchase[] }
  /** The purchase did not happen: the person closed the sheet, or the store refused. */
  | { type: 'iap.failed'; requestId: string; reason: 'cancelled' | 'error'; message?: string }
  /** The store's display price for each store product id. */
  | { type: 'iap.prices'; requestId: string; prices: Record<string, string> }
  /** A position for `geo.get` / `geo.watch` (degrees, metres, epoch ms). */
  | { type: 'geo.position'; requestId: string; latitude: number; longitude: number; accuracy: number; altitude: number | null; heading: number | null; speed: number | null; timestamp: number }
  /** No position: 1 refused, 2 unavailable, 3 timed out — `GeolocationPositionError`'s codes. */
  | { type: 'geo.error'; requestId: string; code: 1 | 2 | 3; message: string }
  /** The compass: degrees from true (else magnetic) north, as iOS's `webkitCompassHeading`. */
  | { type: 'heading'; heading: number; accuracy: number | null }
  /** May the shell use location in the background ("follow me")? */
  | { type: 'location.always'; granted: boolean; reason?: 'permission' | 'error' }
  /** Is the shell watching for arrivals? Refused "Always" is `on: false`, `reason: 'permission'`. */
  | { type: 'arrival.state'; on: boolean; reason?: 'permission' | 'error' }
  /** The share sheet closed: `shared` false when it was dismissed. */
  | { type: 'share.done'; requestId: string; shared: boolean }
  /** Step 99: the phone's answer to `integrity.attest` — or `unavailable` (an older phone, an emulator, an error). */
  | { type: 'integrity.attested'; requestId: string; platform: 'ios'; keyId: string; attestation: string }
  | { type: 'integrity.attested'; requestId: string; platform: 'android'; token: string }
  | { type: 'integrity.unavailable'; requestId: string }
  /** The tickets the app keeps for an account (step 91), the files named, not sent. */
  | { type: 'tickets.list'; requestId: string; tickets: { id: string; editionId: string; ticket: string }[]; files: { id: string; editionId: string; mime: string }[]; raves: string | null;
      /** The app could not read its store (2026-10-30): the lists are empty because of that, not because nothing is kept. */
      failed?: true }
  /** One kept file, or null when the app has none by that id. */
  | { type: 'tickets.file'; requestId: string; id: string; mime: string | null; base64: string | null }
  /** Google's ID token for `google.signin` — or why there is none (the sheet closed, or Google failed). */
  | { type: 'google.signedin'; requestId: string; idToken: string }
  | { type: 'google.failed'; requestId: string; reason: 'cancelled' | 'error'; message?: string }
  /** `app.install`: bytes downloaded of `total` (0 when the server does not say), then the installer opened, or why not. */
  | { type: 'app.install.progress'; requestId: string; done: number; total: number }
  | { type: 'app.install.ready'; requestId: string }
  | { type: 'app.install.failed'; requestId: string; message?: string }
  /** Apple's answer: the token and one-time code, and the name — given only the first time. */
  | { type: 'apple.signedin'; requestId: string; identityToken: string; authorizationCode?: string; givenName?: string; familyName?: string }
  | { type: 'apple.failed'; requestId: string; reason: 'cancelled' | 'error'; message?: string }
  /** Credential Manager's answer to `passkey.get` / `passkey.create`: the credential as WebAuthn JSON. */
  | { type: 'passkey.done'; requestId: string; responseJson: string }
  /** No credential: the sheet closed (`cancelled`), none for this site (`none`), or Android failed (`error`). */
  | { type: 'passkey.failed'; requestId: string; reason: 'cancelled' | 'none' | 'error'; message?: string };

/** At most this many local reminders per rave — iOS keeps 64 pending in all. */
export const MAX_LOCAL_REMINDERS = 48;

/** The always-on card's plan, capped: a day of a big rave, kept small for the shell. */
/** `widget.keep`: at most this many attended raves named. */
export const MAX_WIDGET_KEEP = 500;
export const MAX_CARD_ACTS = 400;
export const MAX_CARD_STAGES = 80;
/** The card's forecast hours — two days at most (the page sends the card's window). */
export const MAX_CARD_WEATHER_HOURS = 48;
const ISO = (v: unknown): v is string => typeof v === 'string' && v.length <= 40 && !Number.isNaN(Date.parse(v));
const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null);

function parseCardPlan(raw: unknown, maxActs = MAX_CARD_ACTS): LiveCardPlan | null {
  if (!raw || typeof raw !== 'object') return null;
  const p = raw as Record<string, unknown>;
  const editionId = short(p.editionId);
  const rave = short(p.rave, 200);
  const timezone = short(p.timezone, 64);
  if (!editionId || !rave || !timezone || !ISO(p.endsAt)) return null;
  if (p.mode !== 'last-stage' && p.mode !== 'follow') return null;
  if (!Array.isArray(p.stages) || !Array.isArray(p.acts) || !Array.isArray(p.saved)) return null;
  const stages: LiveCardStage[] = [];
  for (const r of p.stages.slice(0, MAX_CARD_STAGES)) {
    const x = (r ?? {}) as Record<string, unknown>;
    const id = short(x.id); const name = short(x.name, 200);
    if (!id || !name) continue;
    const cover = typeof x.coverUrl === 'string' && x.coverUrl.length <= 2000 && isWebUrl(x.coverUrl) ? x.coverUrl : null;
    stages.push({ id, name, color: short(x.color, 20) ?? null, lat: num(x.lat), lng: num(x.lng), ...(cover ? { coverUrl: cover } : {}) });
  }
  const acts: LiveCardAct[] = [];
  for (const r of p.acts.slice(0, maxActs)) {
    const x = (r ?? {}) as Record<string, unknown>;
    const id = short(x.id); const name = short(x.name, 200); const stageId = short(x.stageId);
    if (!id || !name || !stageId || !ISO(x.startAt) || !ISO(x.endAt)) continue;
    const photo = typeof x.photoUrl === 'string' && x.photoUrl.length <= 2000 && isWebUrl(x.photoUrl) ? x.photoUrl : null;
    const slug = short(x.slug, 200);
    acts.push({ id, name, stageId, startAt: x.startAt, endAt: x.endAt, photoUrl: photo, ...(slug ? { slug } : {}) });
  }
  const saved = p.saved.filter((v): v is string => short(v) !== undefined).slice(0, 2_000);
  const radius = num(p.radiusM);
  return {
    editionId, rave, timezone, endsAt: p.endsAt as string, mode: p.mode,
    stageId: short(p.stageId) ?? null, radiusM: radius && radius > 0 && radius <= 5_000 ? radius : 100,
    stages, acts, saved,
    ...(typeof p.url === 'string' && p.url.length <= 300 && isWebUrl(p.url) ? { url: p.url } : {}),
    ...(typeof p.logoUrl === 'string' && p.logoUrl.length <= 2000 && isWebUrl(p.logoUrl) ? { logoUrl: p.logoUrl } : {}),
    ...(typeof p.coverUrl === 'string' && p.coverUrl.length <= 2000 && isWebUrl(p.coverUrl) ? { coverUrl: p.coverUrl } : {}),
    // The rave's place — the card's next sunrise or sunset (2026-10-15).
    ...(isLatLng(p.lat, p.lng) ? { lat: p.lat as number, lng: p.lng as number } : {}),
    // The rave's forecast hours — the card's current weather (2026-10-21).
    ...((w) => (w ? { weather: w } : {}))(parseCardWeather(p.weather)),
    // The gates — the card counts down to them before they open (2026-10-22).
    ...((g) => (g && ISO(g.at) && short(g.name, 200) ? { gates: { at: g.at as string, name: short(g.name, 200)! } } : {}))(p.gates as Record<string, unknown> | null | undefined),
    // The card's words in the reader's language (2026-10-27) — only the declared ones, as text.
    ...((w) => (w ? { words: w } : {}))(parseCardWords(p.words)),
  };
}

/** The card's words: each declared one a short string; anything else dropped. None at all: null. */
function parseCardWords(raw: unknown): LiveCardWords | null {
  return parseWords(raw, LIVE_CARD_WORDS);
}

function parseWords<K extends string>(raw: unknown, keys: readonly K[]): Partial<Record<K, string>> | null {
  if (!raw || typeof raw !== 'object') return null;
  const w = raw as Record<string, unknown>;
  const out: Partial<Record<K, string>> = {};
  for (const k of keys) {
    const v = short(w[k], 300);
    if (v) out[k] = v;
  }
  return Object.keys(out).length ? out : null;
}

const frac = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 1 ? v : null);
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * A widget plan (step 79), as declared: the card plan's own checks for the
 * rave, its stages and sets (EVERY set left, up to `MAX_WIDGET_ACTS`), then
 * the stages' map pins, the rave days' weather and the map — each field
 * checked, anything else dropped.
 */
function parseWidgetPlan(raw: unknown): WidgetPlan | null {
  if (!raw || typeof raw !== 'object') return null;
  const p = raw as Record<string, unknown>;
  const base = parseCardPlan({ ...p, acts: [] });
  if (!base || !Array.isArray(p.acts)) return null;
  const gatesAt = p.gatesAt === null ? null : ISO(p.gatesAt) ? p.gatesAt : undefined;
  if (gatesAt === undefined) return null;
  const pins = new Map<string, { x: number | null; y: number | null; trackId?: string }>();
  for (const r of (p.stages as unknown[]).slice(0, MAX_CARD_STAGES)) {
    const x = (r ?? {}) as Record<string, unknown>;
    const trackId = short(x.trackId);
    if (typeof x.id === 'string') pins.set(x.id, { x: frac(x.x), y: frac(x.y), ...(trackId ? { trackId } : {}) });
  }
  // The categories, Music first, as sent (the 0.14.82 walk) — each an id and a label, at most 20.
  const tracks: WidgetTrack[] = [];
  for (const r of (Array.isArray(p.tracks) ? p.tracks : []).slice(0, 20)) {
    const x = (r ?? {}) as Record<string, unknown>;
    const id = short(x.id); const label = short(x.label, 200); const icon = short(x.icon, 16);
    if (id && label) tracks.push({ id, label, ...(icon ? { icon } : {}) });
  }
  const stages: WidgetStage[] = base.stages.map((s) => ({ ...s, ...(pins.get(s.id) ?? {}) }));
  const acts = (parseCardPlan({ ...p, stages: [], acts: (p.acts as unknown[]).slice(0, MAX_WIDGET_ACTS) }, MAX_WIDGET_ACTS)?.acts) ?? [];
  const festivalName = short(p.festivalName, 200);
  const cardWords = parseCardWords(p.cardWords);
  const words = parseWords(p.words, WIDGET_WORDS);
  return {
    editionId: base.editionId, rave: base.rave, timezone: base.timezone, gatesAt, endsAt: base.endsAt,
    mode: base.mode, stageId: base.stageId, radiusM: base.radiusM, stages, acts, saved: base.saved,
    ...(tracks.length ? { tracks } : {}),
    ...(festivalName ? { festivalName } : {}),
    ...(base.url ? { url: base.url } : {}),
    ...(base.logoUrl ? { logoUrl: base.logoUrl } : {}),
    ...(base.coverUrl ? { coverUrl: base.coverUrl } : {}),
    ...(base.lat != null && base.lng != null ? { lat: base.lat, lng: base.lng } : {}),
    ...((w) => (w ? { weather: w } : {}))(parseWidgetWeather(p.weather)),
    ...((m) => (m ? { map: m } : {}))(parseWidgetMap(p.map)),
    ...(cardWords ? { cardWords } : {}),
    ...(words ? { words } : {}),
    ...(p.source === 'list' ? { source: 'list' as const } : {}),
  };
}

function parseWidgetWeather(raw: unknown): WidgetPlan['weather'] | null {
  if (!raw || typeof raw !== 'object') return null;
  const w = raw as Record<string, unknown>;
  const hours = parseCardWeather({ units: w.units, hours: Array.isArray(w.hours) ? w.hours.slice(0, MAX_WIDGET_HOURS) : [] });
  if (!hours) return null;
  const days: WidgetDay[] = [];
  for (const r of Array.isArray(w.days) ? w.days.slice(0, MAX_WIDGET_DAYS) : []) {
    const x = (r ?? {}) as Record<string, unknown>;
    if (typeof x.date !== 'string' || !DAY_RE.test(x.date)) continue;
    if (x.source !== 'FORECAST' && x.source !== 'TYPICAL') continue;
    if (!(WEATHER_CONDITIONS as readonly unknown[]).includes(x.condition)) continue;
    days.push({ date: x.date, source: x.source, highC: num(x.highC), lowC: num(x.lowC), condition: x.condition as WeatherCondition });
  }
  return { units: hours.units, fetchedAt: ISO(w.fetchedAt) ? w.fetchedAt : null, hours: hours.hours, days };
}

function parseWidgetMap(raw: unknown): WidgetPlan['map'] | null {
  if (!raw || typeof raw !== 'object') return null;
  const m = raw as Record<string, unknown>;
  const width = num(m.width); const height = num(m.height);
  if (typeof m.imageUrl !== 'string' || m.imageUrl.length > 2000 || !isWebUrl(m.imageUrl) || !width || !height || width <= 0 || height <= 0) return null;
  const controlPoints = (Array.isArray(m.controlPoints) ? m.controlPoints.slice(0, 50) : []).flatMap((r) => {
    const x = (r ?? {}) as Record<string, unknown>;
    return isLatLng(x.lat, x.lng) && frac(x.x) !== null && frac(x.y) !== null ? [{ lat: x.lat as number, lng: x.lng as number, x: x.x as number, y: x.y as number }] : [];
  });
  const points = (Array.isArray(m.points) ? m.points.slice(0, MAX_WIDGET_POIS) : []).flatMap((r) => {
    const x = (r ?? {}) as Record<string, unknown>;
    const id = short(x.id); const name = short(x.name, 200);
    return id && name && frac(x.x) !== null && frac(x.y) !== null ? [{ id, name, category: short(x.category, 60) ?? null, x: x.x as number, y: x.y as number }] : [];
  });
  return { imageUrl: m.imageUrl, width, height, controlPoints, points };
}

/** The card's forecast hours, as declared: known units and conditions, a real instant, a number — at most two days. */
function parseCardWeather(raw: unknown): LiveCardPlan['weather'] | null {
  if (!raw || typeof raw !== 'object') return null;
  const w = raw as Record<string, unknown>;
  if (w.units !== 'metric' && w.units !== 'imperial') return null;
  if (!Array.isArray(w.hours)) return null;
  const hours: NonNullable<LiveCardPlan['weather']>['hours'] = [];
  for (const r of w.hours) {
    if (hours.length >= MAX_CARD_WEATHER_HOURS) break;
    const x = (r ?? {}) as Record<string, unknown>;
    const tempC = num(x.tempC);
    if (!ISO(x.at) || tempC === null || !(WEATHER_CONDITIONS as readonly unknown[]).includes(x.condition)) continue;
    hours.push({ at: x.at, tempC, condition: x.condition as WeatherCondition });
  }
  return { units: w.units, hours };
}

const TOKEN = /^Expo(nent)?PushToken\[[A-Za-z0-9_-]+\]$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const STORE_ID = /^[a-z0-9][a-z0-9_.]{0,99}$/;
const short = (v: unknown, max = 100) => (typeof v === 'string' && v.length > 0 && v.length <= max ? v : undefined);
const requestIdOf = (o: Record<string, unknown>) => short(o.requestId, 64);
/** The only place the Android app downloads itself from: Expo's build artifacts (`worker/installLinks.ts` checks the same). */
export const APK_URL = /^https:\/\/expo\.dev\/artifacts\/[A-Za-z0-9/_.-]+$/;
/** A ticket's JSON (with its drawing), a file's base64 (15 MB a file) and the raves list, bounded. */
const TICKET_JSON_MAX = 400_000;
const TICKET_FILE_B64_MAX = 21_000_000;
const MIME = /^[a-z]+\/[a-z0-9.+-]{1,60}$/;
/** A Google OAuth client id: `<project number>-<id>.apps.googleusercontent.com`. */
export const GOOGLE_CLIENT = /^[0-9]{1,20}-[0-9a-z]{1,64}\.apps\.googleusercontent\.com$/;
/** WebAuthn options and answers as JSON: a few KB in practice (a registration's attestation is the largest). */
export const PASSKEY_JSON_MAX = 64_000;
/** A JSON OBJECT, bounded — never a bare string or a list. */
const jsonObject = (v: unknown): string | undefined => {
  if (typeof v !== 'string' || v.length === 0 || v.length > PASSKEY_JSON_MAX) return undefined;
  try {
    const o: unknown = JSON.parse(v);
    return o && typeof o === 'object' && !Array.isArray(o) ? v : undefined;
  } catch { return undefined; }
};
const shortList = (v: unknown, max: number) => (Array.isArray(v) ? v.slice(0, max).filter((x): x is string => typeof x === 'string' && x.length > 0 && x.length <= 100) : []);

function parseTickets(o: Record<string, unknown>): ToNative | null {
  const owner = short(o.owner, 64);
  if (!owner) return null;
  if (o.type === 'tickets.put') {
    const id = short(o.id); const editionId = short(o.editionId); const ticket = short(o.ticket, TICKET_JSON_MAX);
    return id && editionId && ticket ? { type: 'tickets.put', owner, id, editionId, ticket } : null;
  }
  if (o.type === 'tickets.file') {
    const id = short(o.id, 200); const editionId = short(o.editionId);
    const mime = typeof o.mime === 'string' && MIME.test(o.mime) ? o.mime : undefined;
    const base64 = typeof o.base64 === 'string' && o.base64.length <= TICKET_FILE_B64_MAX && /^[A-Za-z0-9+/=]*$/.test(o.base64) ? o.base64 : undefined;
    return id && editionId && mime && base64 ? { type: 'tickets.file', owner, id, editionId, mime, base64 } : null;
  }
  if (o.type === 'tickets.drop') return { type: 'tickets.drop', owner, ids: shortList(o.ids, 200), fileIds: shortList(o.fileIds, 400) };
  if (o.type === 'tickets.raves') { const raves = short(o.raves, TICKET_JSON_MAX); return raves ? { type: 'tickets.raves', owner, raves } : null; }
  const requestId = requestIdOf(o);
  if (!requestId) return null;
  if (o.type === 'tickets.list') return { type: 'tickets.list', requestId, owner };
  if (o.type === 'tickets.fileGet') { const id = short(o.id, 200); return id ? { type: 'tickets.fileGet', requestId, owner, id } : null; }
  return null;
}

export function parseToNative(raw: unknown): ToNative | null {
  let m: unknown = raw;
  if (typeof raw === 'string') { try { m = JSON.parse(raw); } catch { return null; } }
  if (!m || typeof m !== 'object') return null;
  const o = m as Record<string, unknown>;
  if (o.type === 'push.register') return { type: 'push.register' };
  if (typeof o.type === 'string' && o.type.startsWith('tickets.')) return parseTickets(o);
  if (o.type === 'orientation' && (o.allow === 'portrait' || o.allow === 'any')) return { type: 'orientation', allow: o.allow };
  if (o.type === 'awake' && typeof o.on === 'boolean') return { type: 'awake', on: o.on };
  if (o.type === 'brightness' && typeof o.raise === 'boolean') return { type: 'brightness', raise: o.raise };
  if (o.type === 'integrity.attest') {
    const requestId = requestIdOf(o); const challenge = short(o.challenge, 200);
    // Google's project number is digits only; anything else is dropped.
    const project = typeof o.cloudProjectNumber === 'string' && /^\d{1,20}$/.test(o.cloudProjectNumber) ? o.cloudProjectNumber : undefined;
    return requestId && challenge ? { type: 'integrity.attest', requestId, challenge, ...(project ? { cloudProjectNumber: project } : {}) } : null;
  }
  if (o.type === 'app.install') {
    const requestId = requestIdOf(o);
    const url = typeof o.url === 'string' && APK_URL.test(o.url) && o.url.length <= 500 ? o.url : undefined;
    return requestId && url ? { type: 'app.install', requestId, url } : null;
  }
  if (o.type === 'google.signin') {
    const requestId = requestIdOf(o);
    // A Google OAuth client id, and nothing else.
    const webClientId = typeof o.webClientId === 'string' && GOOGLE_CLIENT.test(o.webClientId) ? o.webClientId : undefined;
    return requestId && webClientId ? { type: 'google.signin', requestId, webClientId } : null;
  }
  if (o.type === 'apple.signin') {
    const requestId = requestIdOf(o);
    return requestId ? { type: 'apple.signin', requestId } : null;
  }
  if (o.type === 'passkey.get' || o.type === 'passkey.create') {
    const requestId = requestIdOf(o); const requestJson = jsonObject(o.requestJson);
    return requestId && requestJson ? { type: o.type, requestId, requestJson } : null;
  }
  if (o.type === 'share.open') {
    const requestId = requestIdOf(o);
    // A web address only: the sheet must never be handed a `javascript:` or a file.
    const url = typeof o.url === 'string' && /^https?:\/\/\S+$/i.test(o.url) && o.url.length <= 2_000 ? o.url : undefined;
    if (!requestId || !url) return null;
    const title = short(o.title, 200);
    const text = short(o.text, 1_000);
    return { type: 'share.open', requestId, url, ...(title ? { title } : {}), ...(text ? { text } : {}) };
  }
  if (o.type === 'iap.buy') {
    const requestId = requestIdOf(o);
    const product = productById(typeof o.product === 'string' ? o.product : null);
    if (!requestId || !product || typeof o.appAccountToken !== 'string' || !UUID.test(o.appAccountToken)) return null;
    const editionId = short(o.editionId);
    const photoId = short(o.photoId);
    return { type: 'iap.buy', requestId, product: product.id, appAccountToken: o.appAccountToken.toLowerCase(), ...(editionId ? { editionId } : {}), ...(photoId ? { photoId } : {}) };
  }
  if (o.type === 'iap.restore') {
    const requestId = requestIdOf(o);
    return requestId ? { type: 'iap.restore', requestId, ...(o.quiet === true ? { quiet: true } : {}) } : null;
  }
  if (o.type === 'iap.prices') {
    const requestId = requestIdOf(o);
    return requestId ? { type: 'iap.prices', requestId } : null;
  }
  if (o.type === 'iap.finish' && Array.isArray(o.ids)) {
    return { type: 'iap.finish', ids: o.ids.filter((id): id is string => short(id, 200) !== undefined).slice(0, 50) };
  }
  if (o.type === 'iap.manage') return { type: 'iap.manage' };
  if (o.type === 'geo.get' || o.type === 'geo.watch') {
    const requestId = requestIdOf(o);
    return requestId ? { type: o.type, requestId, ...(o.highAccuracy === true ? { highAccuracy: true } : {}) } : null;
  }
  if (o.type === 'geo.clear') {
    const requestId = requestIdOf(o);
    return requestId ? { type: 'geo.clear', requestId } : null;
  }
  if (o.type === 'heading.start') return { type: 'heading.start' };
  if (o.type === 'card.show') {
    const plan = parseCardPlan(o.plan);
    return plan ? { type: 'card.show', plan } : null;
  }
  if (o.type === 'card.end') {
    const editionId = short(o.editionId);
    return editionId ? { type: 'card.end', editionId } : null;
  }
  if (o.type === 'location.always' && typeof o.enable === 'boolean') return { type: 'location.always', enable: o.enable };
  if (o.type === 'session' && typeof o.signedIn === 'boolean') {
    const accountId = o.signedIn ? short(o.accountId) : undefined;
    return { type: 'session', signedIn: o.signedIn, ...(accountId ? { accountId } : {}) };
  }
  if (o.type === 'widget.plan') {
    const plan = parseWidgetPlan(o.plan);
    return plan ? { type: 'widget.plan', plan } : null;
  }
  if (o.type === 'widget.clear') {
    const editionId = short(o.editionId);
    return { type: 'widget.clear', ...(editionId ? { editionId } : {}) };
  }
  if (o.type === 'widget.keep' && Array.isArray(o.editionIds)) {
    const editionIds = o.editionIds.map((v) => short(v)).filter((id): id is string => Boolean(id)).slice(0, MAX_WIDGET_KEEP);
    return { type: 'widget.keep', editionIds };
  }
  if (o.type === 'widget.words') {
    const words = parseWords(o.words, WIDGET_WORDS);
    return words ? { type: 'widget.words', words } : null;
  }
  if (o.type === 'arrival.set' && Array.isArray(o.regions)) {
    return { type: 'arrival.set', regions: o.regions.map(parseArrivalRegion).filter((r): r is ArrivalRegion => r !== null).slice(0, MAX_ARRIVAL_REGIONS) };
  }
  if (o.type === 'reminders.set' && typeof o.editionId === 'string' && Array.isArray(o.reminders)) {
    const reminders: PlannedReminder[] = [];
    for (const r of o.reminders.slice(0, MAX_LOCAL_REMINDERS)) {
      const x = r as Record<string, unknown>;
      if (typeof x?.id === 'string' && typeof x.actId === 'string' && typeof x.at === 'string' && !Number.isNaN(Date.parse(x.at))
        && typeof x.title === 'string' && typeof x.body === 'string') {
        // A tap's address: https and short, or none (the shell still checks it is the platform's — `notificationTarget`).
        const url = typeof x.url === 'string' && x.url.length <= 300 && /^https:\/\/[^\s]+$/.test(x.url) ? x.url : undefined;
        reminders.push({ id: x.id.slice(0, 200), actId: x.actId.slice(0, 100), at: x.at, title: x.title.slice(0, 200), body: x.body.slice(0, 300), ...(url ? { url } : {}) });
      }
    }
    return { type: 'reminders.set', editionId: o.editionId.slice(0, 100), reminders };
  }
  return null;
}

export function parseFromNative(raw: unknown): FromNative | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (o.type === 'location.always' && typeof o.granted === 'boolean') {
    const reason = o.reason === 'permission' || o.reason === 'error' ? o.reason : undefined;
    return { type: 'location.always', granted: o.granted, ...(reason ? { reason } : {}) };
  }
  if (o.type === 'tickets.list' && Array.isArray(o.tickets) && Array.isArray(o.files)) {
    const requestId = requestIdOf(o);
    if (!requestId) return null;
    const tickets = o.tickets.slice(0, 2_000).flatMap((x) => {
      const t = (x ?? {}) as Record<string, unknown>;
      const id = short(t.id); const editionId = short(t.editionId); const ticket = short(t.ticket, TICKET_JSON_MAX);
      return id && editionId && ticket ? [{ id, editionId, ticket }] : [];
    });
    const files = o.files.slice(0, 4_000).flatMap((x) => {
      const f = (x ?? {}) as Record<string, unknown>;
      const id = short(f.id, 200); const editionId = short(f.editionId);
      const mime = typeof f.mime === 'string' && MIME.test(f.mime) ? f.mime : undefined;
      return id && editionId && mime ? [{ id, editionId, mime }] : [];
    });
    return { type: 'tickets.list', requestId, tickets, files, raves: short(o.raves, TICKET_JSON_MAX) ?? null, ...(o.failed === true ? { failed: true as const } : {}) };
  }
  if (o.type === 'tickets.file') {
    const requestId = requestIdOf(o); const id = short(o.id, 200);
    if (!requestId || !id) return null;
    const mime = typeof o.mime === 'string' && MIME.test(o.mime) ? o.mime : null;
    const base64 = typeof o.base64 === 'string' && o.base64.length <= TICKET_FILE_B64_MAX ? o.base64 : null;
    return { type: 'tickets.file', requestId, id, mime: base64 ? mime : null, base64: mime ? base64 : null };
  }
  if (o.type === 'integrity.attested') {
    const requestId = requestIdOf(o);
    if (!requestId) return null;
    if (o.platform === 'ios') {
      const keyId = short(o.keyId, 200); const attestation = short(o.attestation, 20_000);
      return keyId && attestation ? { type: 'integrity.attested', requestId, platform: 'ios', keyId, attestation } : null;
    }
    if (o.platform === 'android') {
      const token = short(o.token, 16_384);
      return token ? { type: 'integrity.attested', requestId, platform: 'android', token } : null;
    }
    return null;
  }
  if (o.type === 'app.install.progress') {
    const requestId = requestIdOf(o);
    const n = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) && v >= 0 ? Math.floor(v) : null);
    const done = n(o.done); const total = n(o.total);
    return requestId && done !== null && total !== null ? { type: 'app.install.progress', requestId, done, total } : null;
  }
  if (o.type === 'app.install.ready') {
    const requestId = requestIdOf(o);
    return requestId ? { type: 'app.install.ready', requestId } : null;
  }
  if (o.type === 'app.install.failed') {
    const requestId = requestIdOf(o);
    const message = typeof o.message === 'string' && o.message.trim() ? o.message.trim().slice(0, 300) : undefined;
    return requestId ? { type: 'app.install.failed', requestId, ...(message ? { message } : {}) } : null;
  }
  if (o.type === 'google.signedin') {
    const requestId = requestIdOf(o); const idToken = short(o.idToken, 8_000);
    return requestId && idToken ? { type: 'google.signedin', requestId, idToken } : null;
  }
  if (o.type === 'google.failed' && (o.reason === 'cancelled' || o.reason === 'error')) {
    const requestId = requestIdOf(o);
    const message = typeof o.message === 'string' && o.message.trim() ? o.message.trim().slice(0, 300) : undefined;
    return requestId ? { type: 'google.failed', requestId, reason: o.reason, ...(message ? { message } : {}) } : null;
  }
  if (o.type === 'apple.signedin') {
    const requestId = requestIdOf(o); const identityToken = short(o.identityToken, 8_000);
    if (!requestId || !identityToken) return null;
    const authorizationCode = short(o.authorizationCode, 1_000);
    const givenName = short(o.givenName); const familyName = short(o.familyName);
    return {
      type: 'apple.signedin', requestId, identityToken,
      ...(authorizationCode ? { authorizationCode } : {}), ...(givenName ? { givenName } : {}), ...(familyName ? { familyName } : {}),
    };
  }
  if (o.type === 'apple.failed' && (o.reason === 'cancelled' || o.reason === 'error')) {
    const requestId = requestIdOf(o);
    const message = typeof o.message === 'string' && o.message.trim() ? o.message.trim().slice(0, 300) : undefined;
    return requestId ? { type: 'apple.failed', requestId, reason: o.reason, ...(message ? { message } : {}) } : null;
  }
  if (o.type === 'passkey.done') {
    const requestId = requestIdOf(o); const responseJson = jsonObject(o.responseJson);
    return requestId && responseJson ? { type: 'passkey.done', requestId, responseJson } : null;
  }
  if (o.type === 'passkey.failed' && (o.reason === 'cancelled' || o.reason === 'none' || o.reason === 'error')) {
    const requestId = requestIdOf(o);
    const message = typeof o.message === 'string' && o.message.trim() ? o.message.trim().slice(0, 300) : undefined;
    return requestId ? { type: 'passkey.failed', requestId, reason: o.reason, ...(message ? { message } : {}) } : null;
  }
  if (o.type === 'integrity.unavailable') {
    const requestId = requestIdOf(o);
    return requestId ? { type: 'integrity.unavailable', requestId } : null;
  }
  if (o.type === 'share.done' && typeof o.shared === 'boolean') {
    const requestId = requestIdOf(o);
    return requestId ? { type: 'share.done', requestId, shared: o.shared } : null;
  }
  if (o.type === 'arrival.state' && typeof o.on === 'boolean') {
    const reason = o.reason === 'permission' || o.reason === 'error' ? o.reason : undefined;
    return { type: 'arrival.state', on: o.on, ...(reason ? { reason } : {}) };
  }
  if (o.type === 'push.denied') {
    const reason = o.reason === 'permission' || o.reason === 'error' ? o.reason : undefined;
    const message = typeof o.message === 'string' && o.message.trim() ? o.message.trim().slice(0, 300) : undefined;
    return { type: 'push.denied', ...(reason ? { reason } : {}), ...(message ? { message } : {}) };
  }
  if (o.type === 'iap.purchases' && (o.platform === 'ios' || o.platform === 'android') && Array.isArray(o.purchases)) {
    const requestId = requestIdOf(o);
    const purchases: StorePurchase[] = [];
    for (const raw of o.purchases.slice(0, 20)) {
      const x = (raw ?? {}) as Record<string, unknown>;
      const id = short(x.id, 200);
      const token = short(x.token, 20_000);
      if (id && token && typeof x.productId === 'string' && STORE_ID.test(x.productId)) purchases.push({ id, productId: x.productId, token });
    }
    return { type: 'iap.purchases', ...(requestId ? { requestId } : {}), platform: o.platform, purchases };
  }
  if (o.type === 'iap.failed') {
    const requestId = requestIdOf(o);
    if (!requestId) return null;
    const message = typeof o.message === 'string' && o.message.trim() ? o.message.trim().slice(0, 300) : undefined;
    return { type: 'iap.failed', requestId, reason: o.reason === 'cancelled' ? 'cancelled' : 'error', ...(message ? { message } : {}) };
  }
  if (o.type === 'iap.prices' && o.prices && typeof o.prices === 'object') {
    const requestId = requestIdOf(o);
    if (!requestId) return null;
    const prices: Record<string, string> = {};
    for (const [k, v] of Object.entries(o.prices as Record<string, unknown>)) {
      if (STORE_ID.test(k) && typeof v === 'string' && v.length <= 40) prices[k] = v;
    }
    return { type: 'iap.prices', requestId, prices };
  }
  if (o.type === 'push.token' && typeof o.token === 'string' && TOKEN.test(o.token) && (o.platform === 'ios' || o.platform === 'android')) {
    return { type: 'push.token', token: o.token, platform: o.platform };
  }
  return null;
}

export function isExpoPushToken(token: string): boolean {
  return TOKEN.test(token);
}

/** A real place on Earth, as numbers. */
function isLatLng(lat: unknown, lng: unknown): boolean {
  return typeof lat === 'number' && typeof lng === 'number' && Number.isFinite(lat) && Number.isFinite(lng)
    && Math.abs(lat) <= 90 && Math.abs(lng) <= 180;
}
